// /home/omenuservice/Fedis/frontend-react/src/components/payments/PaymentModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiPostInstallmentPayment } from '../../api/payments.api';

function fmtMoney(v) {
  const n = Number(v || 0);
  return (Number.isFinite(n) ? n : 0).toLocaleString('fr-FR') + ' FCFA';
}

// ✅ parse robuste: "5 000", "5,000", "5000FCFA" -> 5000
function parseAmount(v) {
  const s = String(v ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(/,/g, '.')
    .replace(/[^\d.]/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function toISODate(dateYYYYMMDD) {
  // évite les décalages de timezone: midi UTC
  if (!dateYYYYMMDD) return new Date().toISOString();
  return new Date(dateYYYYMMDD + 'T12:00:00.000Z').toISOString();
}

const METHODS = [
  { id: 'cash',         label: 'Espèces' },
  { id: 'mobile_money', label: 'Mobile Money' },
  { id: 'card',         label: 'Carte' },
  { id: 'bank',         label: 'Virement' },
];

function PaymentModal({ open, installment, onClose, onPaid }) {
  const dialogRef = useRef(null);

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // ✅ montant dû (source unique)
  const amountDue = useMemo(() => {
    const v = Number(installment?.amount_due ?? installment?.amountDue ?? 0);
    return Number.isFinite(v) ? v : 0;
  }, [installment]);

  // Reset à l'ouverture
  useEffect(() => {
    if (!open) return;
    setErr('');
    setLoading(false);
    setAmount(amountDue > 0 ? String(Math.floor(amountDue)) : '');
    setMethod('cash');
    setNote('');
    setDate(new Date().toISOString().slice(0, 10));
  }, [open, amountDue]);

  // ESC pour fermer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Fermeture si click dehors (backdrop)
  function handleBackdropClick() {
    if (!loading) onClose?.();
  }

  // empêcher la fermeture quand on clique DANS le panneau
  function stop(e) {
    e.stopPropagation();
  }

  const amountParsed = useMemo(() => parseAmount(amount), [amount]);

  const amountPaidAlready = useMemo(() => {
    const v = Number(installment?.amount_paid ?? installment?.amountPaid ?? 0);
    return Number.isFinite(v) ? v : 0;
  }, [installment]);

  const canSubmit = useMemo(() => {
    if (!open || !installment) return false;
    if (loading) return false;
    if (!Number.isFinite(amountParsed) || amountParsed <= 0) return false;
    if (amountParsed > amountDue) return false;
    return true;
  }, [open, installment, loading, amountParsed, amountDue]);

  async function handlePaymentSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;

    try {
      setLoading(true);
      setErr('');

      const installmentId = installment?.installment_id || installment?.installmentId || installment?.id;
      if (!installmentId) throw new Error('installment_id manquant');

      const payload = {
        amount: amountParsed, // ✅ number propre
        payment_method: method || undefined,
        paid_at: toISODate(date),
        note: note?.trim() ? note.trim() : null,
      };

      console.log('🔄 PaymentModal - Payload envoyé:', payload);

      // ✅ UN SEUL APPEL (le helper doit être fiable)
      const response = await apiPostInstallmentPayment(installmentId, payload);

      console.log('✅ PaymentModal - Réponse reçue:', response);

      // ✅ on récupère l'échéance mise à jour (selon ce que renvoie ton API)
      let updated =
        response?.installment ||
        response?.updated?.installment ||
        response?.data?.installment ||
        null;

      // fallback local (si backend renvoie autre chose)
      if (!updated) {
        const paid = Number(amountPaidAlready) + Number(amountParsed);
        const amt = Number(installment?.amount || 0);
        const due = Math.max(0, amt - paid);
        const status =
          paid >= amt ? 'paid' :
          paid > 0    ? 'partial' : 'pending';

        updated = {
          ...installment,
          amount_paid: paid,
          amount_due: due,
          status,
          raw_status: status,
        };
      }

      onPaid?.(updated);
      onClose?.();
    } catch (e2) {
      console.error('❌ PaymentModal - Erreur:', e2);

      // ✅ message d'erreur propre
      let msg = e2?.message || 'Erreur de paiement';
      // si le backend renvoie du JSON stringifié
      try {
        const maybe = JSON.parse(msg);
        if (maybe?.error) msg = maybe.error;
      } catch {}

      setErr(msg);
    } finally {
      setLoading(false);
    }
  }

  function quickFill(pct) {
    if (!amountDue) return;
    const a = Math.floor((amountDue * pct) / 100);
    setAmount(String(a));
  }

  if (!open || !installment) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={handleBackdropClick}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Dialog */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          onClick={stop}
          className="w-full max-w-md bg-surface-card rounded-2xl shadow-xl border"
        >
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-content-primary">Encaisser une tranche</h3>
            <button
              onClick={() => !loading && onClose?.()}
              className="text-content-muted hover:text-content-secondary text-2xl leading-none"
              aria-label="Fermer"
              disabled={loading}
              type="button"
            >
              ×
            </button>
          </div>

          <form onSubmit={handlePaymentSubmit} className="p-5 space-y-4">
            {/* Récap échéance */}
            <div className="bg-surface-primary border rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-content-secondary">Échéance du</span>
                <span className="font-medium">
                  {installment?.due_date
                    ? new Date(installment.due_date).toLocaleDateString('fr-FR')
                    : '—'}
                </span>
              </div>

              <div className="flex justify-between mt-1">
                <span className="text-content-secondary">Montant dû</span>
                <span className="font-semibold">{fmtMoney(amountDue)}</span>
              </div>

              <div className="flex justify-between mt-1">
                <span className="text-content-secondary">Déjà payé</span>
                <span>{fmtMoney(amountPaidAlready)}</span>
              </div>

              {Number.isFinite(amountParsed) && amountParsed > 0 && (
                <div className="flex justify-between mt-1">
                  <span className="text-content-secondary">Reste après paiement</span>
                  <span className="font-medium">
                    {fmtMoney(Math.max(0, amountDue - amountParsed))}
                  </span>
                </div>
              )}
            </div>

            {/* Montant */}
            <div>
              <label className="text-sm text-content-primary">Montant à encaisser</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300"
                  placeholder="0"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setAmount(String(Math.floor(amountDue)))}
                  className="px-3 py-2 rounded-lg border bg-surface-primary hover:bg-surface-primary"
                  disabled={loading || !amountDue}
                >
                  Tout
                </button>
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => quickFill(25)}
                  className="text-xs px-2 py-1 rounded border bg-surface-primary hover:bg-surface-primary"
                  disabled={loading || !amountDue}
                >
                  25%
                </button>
                <button
                  type="button"
                  onClick={() => quickFill(50)}
                  className="text-xs px-2 py-1 rounded border bg-surface-primary hover:bg-surface-primary"
                  disabled={loading || !amountDue}
                >
                  50%
                </button>
                <button
                  type="button"
                  onClick={() => quickFill(75)}
                  className="text-xs px-2 py-1 rounded border bg-surface-primary hover:bg-surface-primary"
                  disabled={loading || !amountDue}
                >
                  75%
                </button>
              </div>

              {/* Erreurs montant */}
              {amount && (!Number.isFinite(amountParsed) || amountParsed <= 0) && (
                <p className="mt-1 text-xs text-red-600">
                  Montant invalide.
                </p>
              )}
              {Number.isFinite(amountParsed) && amountParsed > amountDue && (
                <p className="mt-1 text-xs text-red-600">
                  Le montant ne peut pas dépasser {fmtMoney(amountDue)}.
                </p>
              )}
            </div>

            {/* Méthode */}
            <div>
              <label className="text-sm text-content-primary">Méthode</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 bg-surface-card focus:ring-2 focus:ring-slate-300"
                disabled={loading}
              >
                {METHODS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Date & Note */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-content-primary">Date du paiement</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="text-sm text-content-primary">Note (optionnel)</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Référence, remarque..."
                  className="mt-1 w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Erreur backend */}
            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
                {err}
              </div>
            )}

            {/* Actions */}
            <div className="pt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => !loading && onClose?.()}
                className="px-4 py-2 rounded-lg border bg-surface-card hover:bg-surface-primary"
                disabled={loading}
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="px-4 py-2 rounded-lg text-white bg-slate-900 hover:bg-slate-950 disabled:opacity-50"
              >
                {loading ? 'Encaissement…' : 'Encaisser'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;
